<template>
  <div class="container">
    <div class="blog-title-filters">
      <BaseTitle>{{ $t("blog.title") }}</BaseTitle>
      <div class="filters">
        <CategorySelect
          v-model="selectedCategory"
          class="category-select"
          :placeholder="$t('blog.all-categories')"
        />
        <OrderByToggleButton v-model="selectedOrder" class="order-by-toggle" />
      </div>
    </div>

    <!-- Infinite loading grid -->
    <InfiniteLoading
      ref="infiniteLoading"
      v-slot="{ allData: posts }"
      :data="data?.websitePosts ?? []"
      :has-more-to-load="hasMoreToLoad"
      :pixel-offset="100"
      class="blog-grid"
      @load-more="loadMore"
    >
      <BlogPostIndexItem
        v-for="post in posts"
        :key="post.path"
        class="blog-post-item"
        :post="post"
      />
      <BaseText
        v-if="!fetching && !posts.length"
        class="centered-block no-results-text"
      >
        {{ $t("search.no-results") }}
      </BaseText>
    </InfiniteLoading>
  </div>
</template>

<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import type { ComponentExposed } from "vue-component-type-helpers";
import InfiniteLoading from "~/components/molecules/InfiniteLoading.vue";
import {
  WebsitePostOrderBy,
  useWebsitePostsQuery,
  type WebsitePostOverviewFragment,
} from "~/graphql/generated";

/*
 * Handle the order by
 */
const selectedOrder = ref<"asc" | "desc">("desc");
const orderBy = computed<WebsitePostOrderBy>(() => {
  if (selectedOrder.value === "asc") {
    return WebsitePostOrderBy.PublishedAtAsc;
  } else {
    return WebsitePostOrderBy.PublishedAtDesc;
  }
});

/*
 * Handle the category filtering
 */
const selectedCategory = useRouteQuery("c") as Ref<string | undefined>;
const categorySlugs = computed<string[] | undefined>(() => {
  if (selectedCategory.value) {
    return [selectedCategory.value];
  }
  return undefined;
});

/*
 * Fetch the blog posts
 */
const pagination = reactive({
  offset: 0,
  limit: 20,
});

const variables = computed(() => {
  return {
    websitePosts: {
      orderBy: orderBy.value,
      categorySlugs: categorySlugs.value,
    },
    pagination,
  };
});

const { data, fetching } = await useWebsitePostsQuery({
  variables,
  requestPolicy: "network-only",
});

/*
 * Set up infinite loading
 */
const loadMore = () => {
  if (fetching.value) return;
  pagination.offset += pagination.limit;
};

// Check if there is more data to load by comparing the returned data length to the pagination limit
const hasMoreToLoad = computed(() => {
  return pagination.limit === data.value?.websitePosts.length;
});

// Reset if the category changes
const infiniteLoading = ref<ComponentExposed<
  typeof InfiniteLoading<WebsitePostOverviewFragment>
> | null>(null);
watch(categorySlugs, () => {
  infiniteLoading.value?.resetData();
});
</script>

<style lang="scss" scoped>
.blog-title-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;

  flex-wrap: wrap;

  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: flex-end;
  }

  @include for-small-screen {
    align-items: flex-start;
    flex-direction: column;
    width: 100%;

    .filters {
      width: 100%;
    }

    .category-select,
    .order-by-toggle {
      width: 100%;
    }
  }
}

.blog-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(2, 1fr);

  @include for-small-screen {
    .blog-post-item {
      grid-column: span 2;
    }
  }

  .no-results-text {
    grid-column: 1 / -1;
  }
}
</style>
